import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getHomeContent } from '../../content/homeContent'

/*
 * HeroOrbit — the home page's own visual thesis, in three layers.
 *
 *   core    your operation, where the business pain lives
 *   inner   our senior pods, orbiting it and working from the inside
 *   outer   the full value stream we own, running as a continuous cycle:
 *           discover -> design -> build -> validate -> deliver -> handover
 *
 * The Forward Deployed Engineering page shows a linear pipeline. This shows the
 * thing that page does not: we surround the operation and carry the whole
 * stream, end to end.
 *
 * Lean it 101 pyramid palette: cyan carries the structure, orange marks the
 * stage that is live. Decorative only, and it stands still under reduced motion.
 */

const CX = 330
const CY = 318
const CORE_R = 84
const POD_ORBIT_R = 148
const STAGE_R = 246
const STAGE_NODE_R = 32

const POD_SPIN = '32s'
const STREAM_CYCLE = 9.6 // seconds for one full pass of the value stream

const pointAt = (angleDeg, radius) => {
  const rad = (angleDeg * Math.PI) / 180
  return { x: CX + radius * Math.cos(rad), y: CY + radius * Math.sin(rad) }
}

// Geometry only; every label comes from homeContent hero.orbit, so the
// diagram speaks the visitor's language and the buyer's vocabulary.
const STAGES = [
  { key: 'discover', angle: -90 },
  { key: 'design', angle: -30 },
  { key: 'build', angle: 30 },
  { key: 'validate', angle: 90 },
  { key: 'deliver', angle: 150 },
  { key: 'handover', angle: 210 },
]

const PODS = [0, 180]

const StageIcon = ({ name }) => {
  const s = { fill: 'none', stroke: '#4cbfe0', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' }
  switch (name) {
    case 'discover':
      return (
        <g>
          <circle cx="-2" cy="-2" r="7" {...s} />
          <path d="M3.2 3.2 L8.5 8.5" {...s} />
        </g>
      )
    case 'design':
      return (
        <g>
          <path d="M-8 7.5 L0 -8 L8 7.5 Z" {...s} />
          <path d="M-4.2 0.5 H4.2" {...s} />
        </g>
      )
    case 'build':
      return (
        <g>
          <path d="M-4.5 -6.5 L-10 0 L-4.5 6.5" {...s} />
          <path d="M4.5 -6.5 L10 0 L4.5 6.5" {...s} />
          <path d="M1.5 -7.5 L-1.5 7.5" {...s} />
        </g>
      )
    case 'validate':
      return (
        <g>
          <circle cx="0" cy="0" r="8.2" {...s} />
          <path d="M-4 0.2 L-1.2 3.2 L4.4 -3.4" {...s} />
        </g>
      )
    case 'deliver':
      return (
        <g>
          <path d="M0 7 L0 -6.5" {...s} />
          <path d="M-4.8 -1.8 L0 -7.4 L4.8 -1.8" {...s} />
          <path d="M-7.5 9.5 H7.5" {...s} />
        </g>
      )
    default:
      return (
        <g>
          <path d="M-5.5 -7.5 L-9.5 -7.5 L-9.5 7.5 L-5.5 7.5" {...s} />
          <path d="M-3.5 0 H6" {...s} />
          <path d="M3 -3.4 L6.6 0 L3 3.4" {...s} />
        </g>
      )
  }
}

const PodGlyph = () => (
  <g>
    {[-8, 0, 8].map((x) => (
      <g key={x}>
        <circle cx={x} cy="-4.5" r="2.7" fill="#4cbfe0" />
        <path
          d={`M${x - 4.4} 2.6 a 4.4 4.4 0 0 1 8.8 0`}
          fill="none"
          stroke="#4cbfe0"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </g>
    ))}
  </g>
)

const HeroOrbit = () => {
  const { i18n } = useTranslation()
  const o = getHomeContent(i18n.language).hero.orbit
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = (e) => setReduced(e.matches)
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange) }
  }, [])

  return (
    <div className="w-full">
      <style>{`
        .vs-core { animation: vs-breathe 6s ease-in-out infinite; }
        @keyframes vs-breathe { 0%,100% { opacity:.93 } 50% { opacity:1 } }
        .vs-ring { animation: vs-active ${STREAM_CYCLE}s ease-in-out infinite; }
        @keyframes vs-active {
          0%, 15%, 100% { stroke: rgba(25,162,207,.45); }
          5% { stroke: #f88116; }
        }
        .vs-halo { opacity: 0; animation: vs-glow ${STREAM_CYCLE}s ease-in-out infinite; }
        @keyframes vs-glow {
          0%, 15%, 100% { opacity: 0; }
          5% { opacity: .30; }
        }
        .vs-label { animation: vs-lit ${STREAM_CYCLE}s ease-in-out infinite; }
        @keyframes vs-lit {
          0%, 15%, 100% { fill: #9db4d0; }
          5% { fill: #ffffff; }
        }
        @media (prefers-reduced-motion: reduce) {
          .vs-core, .vs-ring, .vs-halo, .vs-label { animation: none; }
        }
        /* Below sm the svg scales to ~50%, which leaves this type too small to
           read, so it is replaced by real html text underneath. */
        @media (max-width: 639px) {
          .vs-cap { display: none; }
        }
      `}</style>

      <svg
        viewBox="0 0 660 700"
        className="mx-auto w-full max-w-[560px]"
        role="img"
        aria-label={o.aria}
      >
        <defs>
          <radialGradient id="vs-core-g" cx="0.5" cy="0.38" r="0.72">
            <stop offset="0" stopColor="#0f3c4e" />
            <stop offset="1" stopColor="#04141a" />
          </radialGradient>
          <radialGradient id="vs-halo-g" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="rgba(25,162,207,0.18)" />
            <stop offset="1" stopColor="rgba(25,162,207,0)" />
          </radialGradient>
          <filter id="vs-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* ambient depth */}
        <circle cx={CX} cy={CY} r="300" fill="url(#vs-halo-g)" />
        <circle cx={CX} cy={CY} r="196" fill="none" stroke="rgba(25,162,207,.07)" strokeWidth="1" />

        {/* the value stream ring */}
        <circle cx={CX} cy={CY} r={STAGE_R} fill="none" stroke="rgba(25,162,207,.14)" strokeWidth="1" />

        {/* spokes tying the stream back to the operation */}
        {STAGES.map((stage) => {
          const a = pointAt(stage.angle, CORE_R + 16)
          const b = pointAt(stage.angle, STAGE_R - STAGE_NODE_R - 4)
          return (
            <line
              key={`spoke-${stage.key}`}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke="rgba(25,162,207,.10)" strokeWidth="1" strokeDasharray="3 8"
            />
          )
        })}

        {/* direction of flow, readable even when motion is off */}
        {[-60, 0, 60, 120, 180, 240].map((m) => {
          const p = pointAt(m, STAGE_R)
          return (
            <g key={`chev-${m}`} transform={`translate(${p.x} ${p.y}) rotate(${m + 90})`}>
              <path d="M-3.5 -4.5 L2 0 L-3.5 4.5" fill="none" stroke="rgba(76,191,224,.42)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          )
        })}

        {/* the stream running: a bright arc sweeping the cycle */}
        <g transform={`rotate(-90 ${CX} ${CY})`}>
          {!reduced && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              from={`-90 ${CX} ${CY}`}
              to={`270 ${CX} ${CY}`}
              dur={`${STREAM_CYCLE}s`}
              repeatCount="indefinite"
            />
          )}
          <circle
            cx={CX} cy={CY} r={STAGE_R}
            fill="none"
            stroke="#f88116"
            strokeWidth="2"
            strokeLinecap="round"
            strokeOpacity="0.75"
            strokeDasharray={`70 ${2 * Math.PI * STAGE_R - 70}`}
            filter="url(#vs-glow)"
          />
        </g>

        {/* the six stages we own, end to end */}
        {STAGES.map((stage, i) => {
          const p = pointAt(stage.angle, STAGE_R)
          const l = pointAt(stage.angle, STAGE_R + 52)
          const delay = `${-i * (STREAM_CYCLE / STAGES.length)}s`
          return (
            <g key={stage.key}>
              <circle className="vs-halo" cx={p.x} cy={p.y} r={STAGE_NODE_R + 9} fill="#f88116" style={{ animationDelay: delay }} />
              <circle cx={p.x} cy={p.y} r={STAGE_NODE_R} fill="#07181f" fillOpacity="0.97" />
              <circle className="vs-ring" cx={p.x} cy={p.y} r={STAGE_NODE_R} fill="none" stroke="rgba(25,162,207,.45)" strokeWidth="1.6" style={{ animationDelay: delay }} />
              <g transform={`translate(${p.x} ${p.y})`}><StageIcon name={stage.key} /></g>
              <text
                className="vs-label"
                x={l.x} y={l.y + 4}
                textAnchor="middle"
                fill="#9db4d0"
                fontSize="13.5"
                fontWeight="600"
                style={{ animationDelay: delay }}
              >
                {o.stages[i]}
              </text>
            </g>
          )
        })}

        {/* your operation */}
        <g className="vs-core">
          <circle cx={CX} cy={CY} r={CORE_R + 14} fill="none" stroke="rgba(25,162,207,.18)" strokeWidth="1" />
          <circle cx={CX} cy={CY} r={CORE_R} fill="url(#vs-core-g)" stroke="#19a2cf" strokeWidth="1.6" filter="url(#vs-glow)" />
          <text x={CX} y={CY - 26} textAnchor="middle" fill="#8fb0d8" fontSize="11" fontWeight="700" letterSpacing="2.2">{o.center}</text>
          <line x1={CX - 32} y1={CY - 15} x2={CX + 32} y2={CY - 15} stroke="rgba(25,162,207,.35)" strokeWidth="1" />
          {o.items.map((t, i) => (
            <text key={t} x={CX} y={CY + 4 + i * 19} textAnchor="middle" fill="#9db4d0" fontSize="12">{t}</text>
          ))}
        </g>

        {/* our senior pods, orbiting the operation */}
        <circle cx={CX} cy={CY} r={POD_ORBIT_R} fill="none" stroke="rgba(25,162,207,.14)" strokeWidth="1" strokeDasharray="2 10" />
        <g>
          {!reduced && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              from={`0 ${CX} ${CY}`}
              to={`360 ${CX} ${CY}`}
              dur={POD_SPIN}
              repeatCount="indefinite"
            />
          )}
          {PODS.map((angle) => {
            const p = pointAt(angle, POD_ORBIT_R)
            return (
              <g key={angle} transform={`translate(${p.x} ${p.y})`}>
                <g>
                  {!reduced && (
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 0 0"
                      to="-360 0 0"
                      dur={POD_SPIN}
                      repeatCount="indefinite"
                    />
                  )}
                  <circle r="31" fill="#061620" fillOpacity="0.98" stroke="rgba(76,191,224,.45)" strokeWidth="1.4" />
                  <g transform="translate(0 -3)"><PodGlyph /></g>
                  <text y="17" textAnchor="middle" fill="#8fb0d8" fontSize="9" fontWeight="700" letterSpacing="1.1">{o.pod}</text>
                </g>
              </g>
            )
          })}
        </g>

        {/* captions */}
        <text className="vs-cap" x={CX} y="652" textAnchor="middle" fill="#2aa7cf" fontSize="11.5" fontWeight="700" letterSpacing="1.2">
          {o.cap1}
        </text>
        <text className="vs-cap" x={CX} y="676" textAnchor="middle" fill="#7089b0" fontSize="11.5" letterSpacing="1">
          {o.cap2}
        </text>
      </svg>

      {/* On small screens the diagram is decorative, so the words are carried
          here at a size people can actually read. */}
      <div className="-mt-6 sm:hidden">
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.16em] text-[#2aa7cf]">
          {o.cap1}
        </p>
        <p className="mt-2 text-center text-[13px] leading-relaxed text-slate-400">
          {o.stages.join(' · ')}
        </p>
        <p className="mt-1 text-center text-[13px] leading-relaxed text-slate-500">
          {o.cap2}
        </p>
      </div>
    </div>
  )
}

export default HeroOrbit
